/**
 * Setup checks. Pure functions of the loaded data.
 *  - computeEventChecks: the event-level storefront, tabs and gates.
 *  - computeTicketChecks: one ticket type, for its Review step and the
 *    overview list.
 */
import type { TicketingItem } from "../../../types/tickets";
import { parseTicketModifiers, permutationTokenNames } from "../../tokens/variantName";
import { SetupData, CheckTarget, ACCESS_CONTROL_TYPE, isPublicTicket } from "./types";

export type CheckLevel = "error" | "warning" | "ok";

export interface SetupCheck {
  level: CheckLevel;
  title: string;
  body: string;
  step?: CheckTarget;
}

const stripHtml = (s: unknown) => String(s ?? "").replace(/<[^>]*>/g, "").trim();

export function computeEventChecks(data: SetupData): SetupCheck[] {
  const out: SetupCheck[] = [];
  const { event, gates } = data;
  if (!event) return out;

  if (!event.ticketing_slug?.trim()) {
    out.push({ level: "error", title: "No public URL slug", body: "The ticketing page has no address until a slug is set.", step: "event" });
  } else {
    out.push({ level: "ok", title: "Public URL set", body: `ticketing.roninpos.app/events/${event.ticketing_slug}`, step: "event" });
  }
  const proc = event.ticketing_payment_processor_config;
  if (!event.ticketing_payment_processor_id || !proc) {
    out.push({ level: "error", title: "No payment processor", body: "Checkout cannot take a card until a ticketing processor is chosen.", step: "event" });
  } else if (!proc.is_active) {
    out.push({ level: "error", title: "Payment processor is inactive", body: `${proc.processor ?? proc.payment_processor ?? "Processor"} is switched off.`, step: "event" });
  } else {
    const cfg = (proc.config ?? {}) as Record<string, unknown>;
    const isStripe = String(proc.processor ?? proc.payment_processor ?? "").toLowerCase().includes("stripe");
    if (isStripe && !cfg.connected_account_id) {
      out.push({ level: "error", title: "Stripe account not connected", body: "The processor has no connected account id, so payments will fail.", step: "event" });
    } else {
      out.push({ level: "ok", title: "Payment processor configured", body: `${proc.processor ?? proc.payment_processor ?? "Processor"} is selected and active. Keys are not tested; place a test order to confirm.`, step: "event" });
    }
  }
  if (!event.is_active) {
    out.push({ level: "error", title: "Event is inactive", body: "The public page only loads for active events.", step: "event" });
  }
  if (!event.ticketing_background_url) {
    out.push({ level: "warning", title: "No background image", body: "The public page uses the stock background. Optional.", step: "event" });
  }

  const activeTabs = data.tabs.filter((t) => t.is_active);
  const publicTickets = data.tickets.filter(isPublicTicket);
  const emptyTabs = activeTabs.filter((tab) => !publicTickets.some((t) => t.tab_id === tab.id));
  if (emptyTabs.length) {
    out.push({ level: "warning", title: `${emptyTabs.length} empty tab${emptyTabs.length === 1 ? "" : "s"}`, body: `${emptyTabs.map((t) => t.name).join(", ")} will not show on the site until a ticket is placed there.` });
  }

  const accessGates = gates.filter((g) => g.location_type === ACCESS_CONTROL_TYPE);
  if (accessGates.length === 0) {
    out.push({ level: "warning", title: "No gates", body: "No Access Control location exists for this event, so nothing can be scanned. Add one in Event Setup.", step: "event" });
  }
  for (const g of accessGates.filter((x) => !(x.redeemable_tokens ?? []).length)) {
    out.push({ level: "error", title: `${g.name} accepts nothing`, body: "Every scan at this gate fails until it lists at least one ticket type.", step: "event" });
  }
  return out;
}

export function computeTicketChecks(data: SetupData, t: TicketingItem): SetupCheck[] {
  const out: SetupCheck[] = [];
  const isPublic = isPublicTicket(t);
  const groups = parseTicketModifiers(t.modifiers);

  // --- Details ---
  if (!t.is_active) {
    out.push({ level: "warning", title: "Inactive", body: "This ticket does not sell or scan.", step: "details" });
  } else if (t.cashless_top_up_item) {
    out.push({ level: "ok", title: "Wallet top-up item", body: "Sells a balance, not entry.", step: "details" });
  } else if (!t.available_public_sale) {
    out.push({ level: "ok", title: "Comp only", body: "Not on the public page; issued through Ticket Comp.", step: "details" });
  }
  if (isPublic && !(Number(t.price) > 0)) {
    out.push({ level: "warning", title: "Priced at $0 on public sale", body: "Free public tickets are allowed, but check that this is intended.", step: "details" });
  }
  if (isPublic && !stripHtml(t.description)) {
    out.push({ level: "warning", title: "No description", body: "The public page shows \"No description available\". Edit it on the full form.", step: "details" });
  }
  if (isPublic && t.sales_limit_balance != null && Number(t.sales_limit_balance) === 0) {
    out.push({ level: "warning", title: "Sold out", body: "Remaining available is 0, so the public page shows Sold Out.", step: "details" });
  }
  const grantsNothing = t.is_active && t.token_payload && Object.keys(t.token_payload as object).length === 0 && !(Number(t.included_cash_balance) > 0 || Number(t.included_promo_balance) > 0);
  if (grantsNothing) {
    out.push({ level: "warning", title: "Grants nothing", body: "No entry scan, tokens or balance. A buyer gets a QR that does nothing.", step: "details" });
  }
  if (t.is_active && !grantsNothing && (Number(t.price) > 0 || !isPublic) && stripHtml(t.description)) {
    out.push({ level: "ok", title: "Details complete", body: "", step: "details" });
  }

  // --- Modifiers ---
  if (groups.length) {
    const bad = groups.filter((g) => g.options.length < 2);
    if (bad.length) {
      out.push({ level: "warning", title: `${bad.map((g) => g.name).join(", ")} has one option`, body: "A modifier with one option is not a choice. Add options or remove it.", step: "modifiers" });
    } else {
      out.push({ level: "ok", title: `${groups.length} modifier${groups.length === 1 ? "" : "s"}`, body: groups.map((g) => `${g.name} (${g.options.length})`).join(", "), step: "modifiers" });
    }
  }

  // --- Placement ---
  const activeTabs = data.tabs.filter((x) => x.is_active);
  if (isPublic && activeTabs.length > 0 && (!t.tab_id || !t.tab || t.tab.is_active === false)) {
    out.push({ level: "warning", title: "Not on a tab", body: "Shown on the default \"Tickets\" tab at the end of the public page.", step: "placement" });
  } else if (isPublic && t.tab) {
    out.push({ level: "ok", title: `On the ${t.tab.name} tab`, body: "", step: "placement" });
  }

  // --- Gates ---
  if (t.is_active && !t.cashless_top_up_item) {
    const accessGates = data.gates.filter((g) => g.location_type === ACCESS_CONTROL_TYPE);
    const names = new Set<string>([t.name.trim(), ...(groups.length ? permutationTokenNames([t.name], groups) : [])]);
    const accepting = accessGates.filter((g) => (g.redeemable_tokens ?? []).some((n) => names.has(String(n).trim())));
    if (accessGates.length === 0) {
      out.push({ level: "warning", title: "No gates exist", body: "Nothing can scan this ticket until a gate is created. Add one below.", step: "gates" });
    } else if (accepting.length === 0) {
      out.push({ level: "error", title: "No gate accepts this ticket", body: "It sells, but every scan will fail.", step: "gates" });
    } else {
      out.push({ level: "ok", title: `Accepted at ${accepting.length} gate${accepting.length === 1 ? "" : "s"}`, body: accepting.map((g) => g.name).join(", "), step: "gates" });
    }
  }

  return out;
}

export const countByLevel = (checks: SetupCheck[]) => ({
  error: checks.filter((c) => c.level === "error").length,
  warning: checks.filter((c) => c.level === "warning").length,
  ok: checks.filter((c) => c.level === "ok").length,
});
